import React, { useRef } from "react"
import "./OtpCodeInput.css"

// Codes are always this long (backend `roxwrite.otp.length`).
export const OTP_LENGTH = 8
// Backend OTP alphabet: upper-case consonants + digits, no vowels and no "1".
const OTP_DISALLOWED = /[^BCDFGHJKLMNPQRSTVWXYZ023456789]/g

// Force upper-case, strip out-of-alphabet characters and cap at OTP_LENGTH.
export const sanitizeOtp = (value) => (value || "").toUpperCase().replace(OTP_DISALLOWED, "").slice(0, OTP_LENGTH)

/**
 * Segmented OTP entry: renders `length` single-character boxes that together behave
 * like one field. The parent still holds the code as a compact upper-case string
 * (so verify-otp is unchanged); this only changes how it's typed. Handles per-box
 * typing with auto-advance, Backspace/Arrow navigation, and full-code paste or
 * autofill (a browser dumping the whole code into one box spreads across the rest).
 */
const OtpCodeInput = ({ value = "", onChange, disabled, length = OTP_LENGTH, autoFocus = true }) => {
  const inputsRef = useRef([])

  const focusIndex = (i) => {
    const el = inputsRef.current[Math.max(0, Math.min(length - 1, i))]
    if (el) el.focus()
  }

  // Writes `chars` starting at box `i`, so a single character advances one box and an
  // autofilled/pasted code fills the boxes from there on.
  const fillFrom = (i, chars) => {
    const next = sanitizeOtp(value.substring(0, i) + chars + value.substring(i + chars.length))
    onChange(next)
    focusIndex(i + chars.length)
  }

  const handleChange = (i, raw) => {
    const chars = sanitizeOtp(raw)
    if (!chars) return
    // A box that already holds a character receives the typed one, not the old one.
    fillFrom(i, chars.length > 1 ? chars : chars.slice(-1))
  }

  const handleKeyDown = (i, e) => {
    // A filled box is at maxLength, so the browser drops the keystroke and onChange never
    // fires. Overwrite it here, otherwise a wrong character could only be fixed with
    // Backspace first.
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && value[i]) {
      e.preventDefault()
      const char = sanitizeOtp(e.key)
      if (char) fillFrom(i, char)
      return
    }
    if (e.key === "Backspace") {
      e.preventDefault()
      if (value[i]) {
        onChange(sanitizeOtp(value.substring(0, i) + value.substring(i + 1)))
      } else if (i > 0) {
        onChange(sanitizeOtp(value.substring(0, i - 1) + value.substring(i)))
        focusIndex(i - 1)
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault()
      focusIndex(i - 1)
    } else if (e.key === "ArrowRight") {
      e.preventDefault()
      focusIndex(i + 1)
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = sanitizeOtp(e.clipboardData.getData("text"))
    if (!pasted) return
    onChange(pasted)
    focusIndex(pasted.length >= length ? length - 1 : pasted.length)
  }

  return (
    <div className="gs-otp-input" onPaste={handlePaste}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputsRef.current[i] = el }}
          type="text"
          inputMode="text"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          maxLength={1}
          disabled={disabled}
          className="gs-otp-box"
          value={value[i] || ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          autoFocus={autoFocus && i === 0}
          aria-label={`Code character ${i + 1}`}
        />
      ))}
    </div>
  )
}

export default OtpCodeInput
